"use client"
import { CommodityLabels } from "@tc/ui-dfe/utils/constants"
import {
  SearchFieldCollectionMappings,
  SearchFieldId,
  useTypesenseSearch,
} from "@tc/ui-typesense-client"
import { usePathname } from "next/navigation"
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { useConfirm } from "../useConfirm"

interface MatchedDestinationDocument {
  name: string
  id: string
}

interface DraftProtectionContextType {
  isDirty: boolean
  requestType?: "export" | "transfer"
  commodity?: string
  destination?: string
  setDraftState: (state: {
    isDirty: boolean
    requestType?: "export" | "transfer"
    commodity?: string
    destinationId?: string
  }) => void
  confirmDiscardDraft: () => Promise<boolean>
}

const DraftProtectionContext = createContext<DraftProtectionContextType>({
  isDirty: false,
  setDraftState: () => {},
  confirmDiscardDraft: async () => true,
})

export function DraftProtectionProvider({ children }: { children: ReactNode }) {
  const [isDirty, setIsDirty] = useState(false)
  const [requestType, setRequestType] = useState<"export" | "transfer">()
  const [commodity, setCommodity] = useState<string>()
  const [destinationId, setDestinationId] = useState<string>()
  const [destination, setDestination] = useState<string>()
  const { confirm } = useConfirm()
  const pathname = usePathname()

  const { document: destinationDocument } = useTypesenseSearch({
    value: destinationId || "",
    collectionName: SearchFieldCollectionMappings[SearchFieldId.CountryCode],
    useDefaultCollectionAttributes: true,
  })

  useEffect(() => {
    if (destinationDocument) {
      const name = (destinationDocument as MatchedDestinationDocument)?.name
      setDestination(name)
    }
  }, [destinationDocument, destinationId])

  const setDraftState = useCallback(
    ({
      isDirty: dirty,
      requestType: type,
      commodity: comm,
      destinationId: destId,
    }: {
      isDirty: boolean
      requestType?: "export" | "transfer"
      commodity?: string
      destinationId?: string
    }) => {
      setIsDirty(dirty)
      setRequestType(type)
      setCommodity(comm)
      setDestinationId(destId)
    },
    [],
  )

  const confirmDiscardDraft = async (): Promise<boolean> => {
    if (!isDirty) return true

    const isProductDetailsPage = pathname?.includes("/products")

    let modalMessage =
      "You have unsaved changes. Are you sure you want to leave this page?"

    if (requestType === "export") {
      const commodityLabel = commodity
        ? (CommodityLabels[commodity] ?? commodity)
        : "commodity"
      const destinationLabel = destination || destinationId || "destination"
      modalMessage = `Are you sure you want to discard export for ${commodityLabel} to ${destinationLabel}? You can't undo this action.`
    } else if (requestType === "transfer") {
      modalMessage =
        "Are you sure you want to discard this transfer? You can't undo this action."
    }

    const confirmed = await confirm({
      title: isProductDetailsPage
        ? "Unsaved product details"
        : "Discard draft request?",
      message: isProductDetailsPage
        ? "You've made changes in product details. Going back to export request will discard your unsaved data. What would you like to do?"
        : modalMessage,
      confirmLabel: isProductDetailsPage
        ? "Discard unsaved changes"
        : "Discard Draft",
      cancelLabel: isProductDetailsPage ? "Stay here" : "Cancel",
    })

    if (confirmed) setDraftState({ isDirty: false })

    return Boolean(confirmed)
  }

  return (
    <DraftProtectionContext.Provider
      value={{
        isDirty,
        requestType,
        commodity,
        destination,
        setDraftState,
        confirmDiscardDraft,
      }}
    >
      {children}
    </DraftProtectionContext.Provider>
  )
}

export function useDraftProtection() {
  return useContext(DraftProtectionContext)
}
